import { useMemo, useState } from "react"
import { EnvelopeIcon, MagnifyingGlassIcon } from "@heroicons/react/24/outline"
import { useEmailLog, type EmailLogFilters, type EmailLogItem } from "@/hooks/useEmailLog"

// Status enum from email_events; kept in sync with the backend schema.
const STATUSES = ["queued", "sent", "delivered", "bounced", "complained", "failed"] as const

// Template types from sendAndLogEmail's EmailType union (backend lib/send-email.ts).
// Mirrors sendAndLogEmail's EmailType union (backend/src/lib/send-email.ts), in
// the same order. Nothing enforces the mirror, so it had drifted — login_code,
// reengagement_nudge and support_reply were all being sent but were unfilterable
// here. When you add a type to the union, add it here too.
const TYPES = [
  "registration",
  "email_verification",
  "password_reset",
  "login_code",
  "team_invitation",
  "team_added",
  "invitation_accepted",
  "invitation_declined",
  "game_exchange_invitation",
  "trade_request",
  "trade_response_received",
  "trade_completed",
  "team_film_ready",
  "team_film_announce",
  "reengagement_nudge",
  "community_invitation",
  "invite_reminder",
  "welcome_offer_setup",
  "welcome_offer_reminder",
  "export_complete",
  "export_failed",
  "support_reply",
  "affiliate_referral",
  "affiliate_statement",
  "admin_compose",
] as const

const WINDOWS: Array<{ label: string; days: number | undefined }> = [
  { label: "All time", days: undefined },
  { label: "Last 24h", days: 1 },
  { label: "Last 7 days", days: 7 },
  { label: "Last 30 days", days: 30 },
  { label: "Last 90 days", days: 90 },
]

// Same mapping as the per-invite badge on TeamDetail: delivered = good, the
// red states are the real "never landed" answers, sent/queued are in-flight.
function statusBadge(status: string): { label: string; className: string } {
  switch (status) {
    case "delivered":
      return { label: "Delivered", className: "bg-green-100 text-green-800" }
    case "bounced":
      return { label: "Bounced", className: "bg-red-100 text-red-800" }
    case "complained":
      return { label: "Complained", className: "bg-red-100 text-red-800" }
    case "failed":
      return { label: "Send failed", className: "bg-red-100 text-red-800" }
    case "sent":
      return { label: "Sent", className: "bg-blue-100 text-blue-800" }
    case "queued":
      return { label: "Queued", className: "bg-blue-100 text-blue-800" }
    default:
      return { label: status, className: "bg-gray-100 text-gray-800" }
  }
}

// email_events timestamps are UTC "YYYY-MM-DD HH:MM:SS" with no zone marker; make
// the space an ISO "T" and pin UTC so the browser doesn't read them as local.
function formatTimestamp(value: string): string {
  const iso = value.includes("T") ? value : value.replace(" ", "T")
  const date = new Date(iso.endsWith("Z") ? iso : `${iso}Z`)
  if (Number.isNaN(date.getTime())) return value
  return date.toLocaleString()
}

const SELECT_CLASS =
  "rounded-md border border-border bg-background px-2.5 py-1.5 text-sm text-primary focus:outline-none focus:ring-1 focus:ring-primary"

export function EmailLog() {
  const [status, setStatus] = useState<string>("")
  const [type, setType] = useState<string>("")
  const [windowDays, setWindowDays] = useState<number | undefined>(undefined)
  // `qInput` is what the user is typing; `q` is what we actually query (committed
  // on submit) so we don't refetch on every keystroke.
  const [qInput, setQInput] = useState<string>("")
  const [q, setQ] = useState<string>("")

  const filters: EmailLogFilters = useMemo(
    () => ({
      status: status || undefined,
      type: type || undefined,
      q: q || undefined,
      window: windowDays,
    }),
    [status, type, q, windowDays]
  )

  const { data, isLoading, isError, fetchNextPage, hasNextPage, isFetchingNextPage, isFetching } =
    useEmailLog(filters)

  const items: EmailLogItem[] = useMemo(
    () => data?.pages.flatMap((page) => page.items) ?? [],
    [data]
  )

  return (
    <div className="space-y-6">
      <p className="text-secondary">
        Every transactional email Playside sent, with its delivery status. From{" "}
        <code className="rounded bg-background px-1.5 py-0.5 font-mono text-xs">email_events</code>.
      </p>

      {/* Filters */}
      <form
        className="flex flex-wrap items-center gap-2"
        onSubmit={(e) => {
          e.preventDefault()
          setQ(qInput.trim())
        }}
      >
        <div className="relative">
          <MagnifyingGlassIcon className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-secondary" />
          <input
            type="text"
            value={qInput}
            onChange={(e) => setQInput(e.target.value)}
            placeholder="Search recipient email…"
            className="w-64 rounded-md border border-border bg-background py-1.5 pl-8 pr-2.5 text-sm text-primary placeholder:text-secondary focus:outline-none focus:ring-1 focus:ring-primary"
          />
        </div>
        <select
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          className={SELECT_CLASS}
          aria-label="Filter by status"
        >
          <option value="">All statuses</option>
          {STATUSES.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
        <select
          value={type}
          onChange={(e) => setType(e.target.value)}
          className={SELECT_CLASS}
          aria-label="Filter by type"
        >
          <option value="">All types</option>
          {TYPES.map((t) => (
            <option key={t} value={t}>
              {t}
            </option>
          ))}
        </select>
        <select
          value={windowDays ?? ""}
          onChange={(e) => setWindowDays(e.target.value ? Number(e.target.value) : undefined)}
          className={SELECT_CLASS}
          aria-label="Filter by time window"
        >
          {WINDOWS.map((w) => (
            <option key={w.label} value={w.days ?? ""}>
              {w.label}
            </option>
          ))}
        </select>
        <button
          type="submit"
          className="rounded-md border border-border bg-surface px-3 py-1.5 text-sm text-primary hover:bg-background"
        >
          Search
        </button>
        {(q || status || type || windowDays !== undefined) && (
          <button
            type="button"
            onClick={() => {
              setQInput("")
              setQ("")
              setStatus("")
              setType("")
              setWindowDays(undefined)
            }}
            className="text-sm text-secondary hover:text-primary hover:underline"
          >
            Clear
          </button>
        )}
      </form>

      {/* Table */}
      <div className="overflow-hidden rounded-lg border border-border bg-surface">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-left text-secondary">
                <th className="px-4 py-2.5 font-medium">Sent</th>
                <th className="px-4 py-2.5 font-medium">To</th>
                <th className="px-4 py-2.5 font-medium">Type</th>
                <th className="px-4 py-2.5 font-medium">Status</th>
                <th className="px-4 py-2.5 font-medium">Subject</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td colSpan={5} className="px-4 py-8 text-center text-secondary">
                    Loading…
                  </td>
                </tr>
              ) : isError ? (
                <tr>
                  <td colSpan={5} className="px-4 py-8 text-center text-rose-400">
                    Failed to load the email log.
                  </td>
                </tr>
              ) : items.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-4 py-8 text-center text-secondary">
                    No emails match these filters.
                  </td>
                </tr>
              ) : (
                items.map((item) => {
                  const badge = statusBadge(item.status)
                  return (
                    <tr key={item.publicId} className="border-b border-border/50 align-top">
                      <td className="whitespace-nowrap px-4 py-2.5 text-secondary">
                        {formatTimestamp(item.createdAt)}
                      </td>
                      <td className="px-4 py-2.5 font-mono text-xs text-primary">{item.toEmail}</td>
                      <td className="whitespace-nowrap px-4 py-2.5 text-secondary">{item.type}</td>
                      <td className="px-4 py-2.5">
                        <span
                          className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${badge.className}`}
                          title={item.error ?? undefined}
                        >
                          {badge.label}
                        </span>
                      </td>
                      <td className="px-4 py-2.5 text-secondary">
                        {item.subject ?? <span className="text-secondary/60">—</span>}
                      </td>
                    </tr>
                  )
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      <div className="flex items-center justify-between">
        <p className="text-xs text-secondary">
          <EnvelopeIcon className="mr-1 inline h-3.5 w-3.5" />
          Showing {items.length} email{items.length === 1 ? "" : "s"}
          {isFetching && !isFetchingNextPage ? " · refreshing…" : ""}
        </p>
        {hasNextPage && (
          <button
            type="button"
            onClick={() => fetchNextPage()}
            disabled={isFetchingNextPage}
            className="rounded-md border border-border bg-surface px-3 py-1.5 text-sm text-primary hover:bg-background disabled:opacity-50"
          >
            {isFetchingNextPage ? "Loading…" : "Load more"}
          </button>
        )}
      </div>
    </div>
  )
}
